import React, { useRef, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardProps,
  Divider,
  Flex,
  Group,
  Loader,
  Menu,
  Switch,
  Text,
  TextInput,
  Title,
  useMantineTheme
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconAlertHexagon,
  IconCheck,
  IconCloudComputing,
  IconDotsVertical,
  IconExternalLink,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
  IconRosetteDiscountCheck,
  IconTrash
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { DroxyModal } from 'components/Droxy/DroxyModal';
import { ResourceConflictErrorModal } from 'components/ResourceConflictErrorModal';
import { DNSRecordTable } from 'pages/Settings/SettingTabs/WhiteLabelSettingsTab/DNSRecordTable';
import { WhiteLabelSubDomainsAccordion } from 'pages/Settings/SettingTabs/WhiteLabelSettingsTab/WhiteLabelSubDomainsAccordion';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { WhiteLabelService } from 'services/white-label-service';
import { WHITE_LABEL_DOMAINS_QUERY_KEY, WHITE_LABEL_DOMAIN_QUERY_KEY } from 'shared/constants/query-keys';
import {
  MAX_SUB_DOMAINS,
  ROOT_PREFIX,
  SUB_DOMAIN_PREFIX_MAX_LENGTH,
  WHITE_LABEL_DOMAIN_DOCS_URL
} from 'shared/constants/white-label-constant';
import { DeleteElementConflictDto } from 'shared/interfaces/General';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { WhiteLabelDomain, WhiteLabelDomainStatus } from 'shared/interfaces/WhiteLabelDomain';
import { buildDomainUrl } from 'shared/utils/domain';
import { maxLengthValidator, requiredInputValidator, subDomainPrefixValidator } from 'shared/utils/form-validators';
import { formatDate } from 'shared/utils/format';
import { formatWhiteLabelDomainStatusMessage } from 'shared/utils/white-label';

const REFRESH_INTERVAL_MS = 10000; // 10 seconds

interface WhiteLabelDomainCardProps extends Omit<CardProps, 'children'> {
  domain: WhiteLabelDomain;
}

export function WhiteLabelDomainCard({ domain, ...cardProps }: WhiteLabelDomainCardProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();

  const [conflictedSmartAssistants, setConflictedSmartAssistants] = useState<SmartAssistant[]>([]);
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [verificationDNSRecordModalOpened, setVerificationDNSRecordModalOpened] = useState(false);
  const [addSubDomainModalOpened, setAddSubDomainModalOpened] = useState(false);
  const openedSubDomainPrefixRef = useRef<string | null>(null);

  const shouldRefresh = (domain: WhiteLabelDomain) => {
    // Refetch all statuses except AVAILABLE OR FAILED
    return domain.status !== WhiteLabelDomainStatus.AVAILABLE && domain.status !== WhiteLabelDomainStatus.FAILED;
  };

  const addSubDomainForm = useForm({
    initialValues: { prefix: '' },
    validate: {
      prefix: (value) =>
        requiredInputValidator(value) ||
        subDomainPrefixValidator(value) ||
        maxLengthValidator(value, SUB_DOMAIN_PREFIX_MAX_LENGTH)
    }
  });

  const refreshDomainQuery = useQuery({
    queryFn: () => WhiteLabelService.refreshWhiteLabelDomain(domain._id),
    queryKey: [WHITE_LABEL_DOMAIN_QUERY_KEY, { domainId: domain._id }],
    onSuccess: (refreshedDomain: WhiteLabelDomain) => {
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
        existingDomains.map((existingDomain) => (existingDomain._id === domain._id ? refreshedDomain : existingDomain))
      );
    },
    refetchInterval: () => (shouldRefresh(domain) ? REFRESH_INTERVAL_MS : 0)
  });

  const toggleRootDomainMutation = useMutation({
    mutationFn: (isActive: boolean) => {
      if (isActive) return WhiteLabelService.addWhiteLabelSubDomain({ prefix: '', domainId: domain._id });
      return WhiteLabelService.deleteWhiteLabelSubDomain({ prefix: '', domainId: domain._id });
    },
    onSuccess: (updatedDomain: WhiteLabelDomain, isActiveParam: boolean) => {
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
        existingDomains.map((existingDomain) => (existingDomain._id === domain._id ? updatedDomain : existingDomain))
      );
      NotificationService.showResult(
        true,
        `FEED_BACK.WHITE_LABEL.SUCCESS.ROOT_DOMAIN_${isActiveParam ? '' : 'DE'}ACTIVATED`
      );
    },
    onError: (error: AxiosError, isActiveParam: boolean) => {
      handleConflictError(error);
      NotificationService.showResult(
        false,
        `FEED_BACK.WHITE_LABEL.FAILURE.ROOT_DOMAIN_${isActiveParam ? '' : 'DE'}ACTIVATED`
      );
    }
  });

  const addSubDomainMutation = useMutation({
    mutationFn: WhiteLabelService.addWhiteLabelSubDomain,
    onSuccess: (updatedDomain: WhiteLabelDomain) => {
      closeModal();
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
        existingDomains.map((existingDomain) => (existingDomain._id === domain._id ? updatedDomain : existingDomain))
      );
      NotificationService.showResult(true, 'FEED_BACK.WHITE_LABEL.SUCCESS.ADD_SUB_DOMAIN');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.WHITE_LABEL.FAILURE.ADD_SUB_DOMAIN')
  });

  const deleteDomainMutation = useMutation({
    mutationFn: WhiteLabelService.deleteWhiteLabelDomain,
    onSuccess: () => {
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
        existingDomains.filter((existingDomain) => existingDomain._id !== domain._id)
      );
      NotificationService.showResult(true, 'FEED_BACK.WHITE_LABEL.SUCCESS.DELETE_DOMAIN');
    },
    onError: (error: AxiosError) => {
      handleConflictError(error);
      NotificationService.showResult(false, 'FEED_BACK.WHITE_LABEL.FAILURE.DELETE_DOMAIN');
    }
  });

  const retryDomainMutation = useMutation({
    mutationFn: WhiteLabelService.retryWhiteLabelDomain,
    onSuccess: (retriedDomain: WhiteLabelDomain, whiteLabelDomainId: string) => {
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) =>
        existingDomains.map((existingDomain) =>
          existingDomain._id === whiteLabelDomainId ? retriedDomain : existingDomain
        )
      );
      NotificationService.showResult(true, 'FEED_BACK.WHITE_LABEL.SUCCESS.RETRY_DOMAIN');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.TooManyRequests) return;
      handleConflictError(error);
      NotificationService.showResult(false, 'FEED_BACK.WHITE_LABEL.FAILURE.RETRY_DOMAIN');
    }
  });

  const closeModal = () => {
    addSubDomainForm.reset();
    setAddSubDomainModalOpened(false);
  };

  const handleConflictError = (error: AxiosError) => {
    if (error.response?.status == HttpStatusCode.Conflict) {
      const data = error.response?.data as DeleteElementConflictDto;
      const conflictedSmartAssistants = data.smartAssistants;
      if (conflictedSmartAssistants && conflictedSmartAssistants.length > 0)
        setConflictedSmartAssistants(conflictedSmartAssistants);

      setErrorModalOpened(true);
    }
  };

  const StatusBadge = () => {
    switch (domain.status) {
      case WhiteLabelDomainStatus.AVAILABLE:
        return (
          <Badge
            color="green"
            tt="capitalize"
            leftSection={
              <Flex h="100%" align="center" justify="center">
                <IconCheck style={{ width: 16, height: 16 }} />
              </Flex>
            }>
            {t('WHITE_LABEL.STATUS.AVAILABLE')}
          </Badge>
        );
      case WhiteLabelDomainStatus.UPDATING:
      case WhiteLabelDomainStatus.PENDING_DEPLOYMENT:
      case WhiteLabelDomainStatus.CREATING:
        return (
          <Badge
            color="blue"
            tt="capitalize"
            leftSection={
              <Flex h="100%" align="center" justify="center">
                <Loader size={10} color="blue" />
              </Flex>
            }>
            {t(`WHITE_LABEL.STATUS.${domain.status}`)}
          </Badge>
        );
      case WhiteLabelDomainStatus.PENDING_VERIFICATION:
        return (
          <Badge
            color="yellow"
            tt="capitalize"
            leftSection={
              <Flex h="100%" align="center" justify="center">
                <IconAlertHexagon style={{ width: 16, height: 16 }} />
              </Flex>
            }>
            {t('WHITE_LABEL.STATUS.PENDING_VERIFICATION')}
          </Badge>
        );
      case WhiteLabelDomainStatus.FAILED:
        return (
          <Badge tt="capitalize" color="red">
            {t('WHITE_LABEL.STATUS.FAILED')}
          </Badge>
        );

      default:
        return <></>;
    }
  };

  const CreatingOrPendingDeploymentContent = () => {
    return (
      <Flex justify="center" align="center" direction="column" h="100%" maw={600} mx="auto" mih={200} gap={10}>
        <Flex
          p="sm"
          direction="column"
          align="center"
          justify="center"
          style={{
            backgroundColor: theme.other.gray.bg,
            borderRadius: 10
          }}>
          <Loader variant="bars" size={20} />
        </Flex>

        <Title order={4}>{t(`WHITE_LABEL.${domain.status}.TITLE`)}</Title>
        <Text c="dimmed" maw="55%" align="center">
          {t(`WHITE_LABEL.${domain.status}.DESCRIPTION`)}
        </Text>
      </Flex>
    );
  };

  const PendingVerificationContent = () => {
    if (!domain.certificateVerificationDNSRecord) return <></>;

    return (
      <DNSRecordTable
        record={domain.certificateVerificationDNSRecord}
        alert={
          <Alert
            icon={<IconInfoCircle size="1rem" />}
            title={t('WHITE_LABEL.PENDING_VERIFICATION.TITLE')}
            color="yellow">
            <Text color="yellow">{t('WHITE_LABEL.PENDING_VERIFICATION.DESCRIPTION')}</Text>
          </Alert>
        }
      />
    );
  };

  const FailedContent = () => {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title={
          <Flex direction="row" align="center" justify="space-between" w="100%">
            <Text mb={7}>{t('WHITE_LABEL.STATUS.FAILED')}</Text>
            <Button
              size="xs"
              variant="subtle"
              loading={retryDomainMutation.isLoading}
              leftIcon={<IconRefresh />}
              onClick={() => retryDomainMutation.mutate(domain._id)}>
              {t('WHITE_LABEL.FAILED.RETRY')}
            </Button>
          </Flex>
        }
        color="red"
        styles={{
          label: {
            width: '100%'
          }
        }}>
        <Text color="red">
          {formatWhiteLabelDomainStatusMessage(domain.statusMessage || t('WHITE_LABEL.FAILED.FALLBACK_ERROR'))}
        </Text>
      </Alert>
    );
  };

  const AvailableOrUpdatingContent = () => {
    const rootSubDomain = domain.subdomains.find((subDomain) => subDomain.prefix === '');

    return (
      <Box>
        <Flex direction="row" align="center" justify="space-between">
          <Box>
            <Title order={5}>{t('WHITE_LABEL.ROOT_DOMAIN.TITLE')}</Title>
            <Text c="dimmed" fz="sm">
              {t('WHITE_LABEL.ROOT_DOMAIN.DESCRIPTION', { domain: domain.value })}
            </Text>
          </Box>

          <Switch
            checked={!!rootSubDomain}
            disabled={toggleRootDomainMutation.isLoading}
            size="sm"
            onChange={(event) => toggleRootDomainMutation.mutate(event.currentTarget.checked)}
            thumbIcon={toggleRootDomainMutation.isLoading ? <Loader size={10} /> : undefined}
          />
        </Flex>

        {rootSubDomain && (
          <DNSRecordTable
            mt="lg"
            record={rootSubDomain.record}
            alert={
              <Alert
                p="xs"
                icon={<IconInfoCircle />}
                title={t('WHITE_LABEL.RECORDS.INFO', {
                  recordType: rootSubDomain.record.type,
                  subDomain: buildDomainUrl(ROOT_PREFIX, domain.value, false)
                })}
                color="blue"
                styles={{
                  title: { marginBottom: 0, fontWeight: 500, marginTop: 2.5 }
                }}>
                {null}
              </Alert>
            }
          />
        )}

        <Divider my="xl" />

        <Flex direction="row" align="center" justify="space-between">
          <Box>
            <Title order={5}>{t('WHITE_LABEL.SUB_DOMAINS.TITLE')}</Title>
            <Text c="dimmed" fz="sm">
              {t('WHITE_LABEL.SUB_DOMAINS.DESCRIPTION', { domain: domain.value })}
            </Text>
          </Box>
          {domain.subdomains.length < MAX_SUB_DOMAINS && (
            <Button
              leftIcon={<IconPlus />}
              size="xs"
              variant="outline"
              onClick={() => setAddSubDomainModalOpened(true)}>
              {t('APP.ACTIONS.ADD')}
            </Button>
          )}
        </Flex>

        <WhiteLabelSubDomainsAccordion
          domain={domain}
          openedSubDomainPrefix={openedSubDomainPrefixRef.current || undefined}
          onToggleSubDomainPrefix={(subDomainPrefix) => (openedSubDomainPrefixRef.current = subDomainPrefix)}
        />
      </Box>
    );
  };

  const CardSectionContent = () => {
    switch (domain.status) {
      case WhiteLabelDomainStatus.CREATING:
      case WhiteLabelDomainStatus.PENDING_DEPLOYMENT:
        return <CreatingOrPendingDeploymentContent />;
      case WhiteLabelDomainStatus.PENDING_VERIFICATION:
        return <PendingVerificationContent />;
      case WhiteLabelDomainStatus.FAILED:
        return <FailedContent />;
      case WhiteLabelDomainStatus.AVAILABLE:
      case WhiteLabelDomainStatus.UPDATING:
        return <AvailableOrUpdatingContent />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <Card p="xs" withBorder {...cardProps}>
        <Card.Section withBorder p="xl" style={{ borderBottom: `0.5px solid ${theme.colors.gray[2]}` }}>
          <Group position="apart">
            <Flex direction="row" align="center" gap={10}>
              <Title order={4}>{domain.value}</Title>
              <StatusBadge />
            </Flex>
            {deleteDomainMutation.isLoading ? (
              <Flex align="center" justify="center" w={28} h={28}>
                <Loader size={20} />
              </Flex>
            ) : (
              <Flex direction="row" align="center" gap={10}>
                {domain.lastRefreshedAt && (
                  <Text c="dimmed" fz="xs">
                    {t('WHITE_LABEL.LAST_REFRESHED_AT', {
                      date: formatDate(domain.lastRefreshedAt, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    })}
                  </Text>
                )}
                <Menu offset={{ crossAxis: -75 }}>
                  <Menu.Target>
                    <ActionIcon color="gray">
                      <IconDotsVertical />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      icon={<IconRosetteDiscountCheck />}
                      onClick={() => setVerificationDNSRecordModalOpened(true)}>
                      {t('WHITE_LABEL.VERIFICATION_RECORD.VIEW_VERIFICATION_RECORD')}
                    </Menu.Item>

                    <Menu.Item icon={<IconRefresh />} onClick={() => refreshDomainQuery.refetch()}>
                      {t('APP.ACTIONS.REFRESH')}
                    </Menu.Item>

                    <Menu.Item color="red" icon={<IconTrash />} onClick={() => deleteDomainMutation.mutate(domain._id)}>
                      {t('APP.ACTIONS.DELETE')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            )}
          </Group>
        </Card.Section>

        <Card.Section py="xl" px="lg">
          <CardSectionContent />
        </Card.Section>

        <Card.Section p="xl" bg="gray.0">
          <Group position="apart">
            <Text c="dimmed">{t('WHITE_LABEL.HELP.DESCRIPTION')}</Text>
            <Button
              variant="subtle"
              size="xs"
              leftIcon={<IconExternalLink />}
              onClick={() => window.open(WHITE_LABEL_DOMAIN_DOCS_URL, '_blank')}>
              {t('WHITE_LABEL.HELP.CTA')}
            </Button>
          </Group>
        </Card.Section>
      </Card>
      <DroxyModal
        size={500}
        titleIcon={<IconCloudComputing />}
        title={t('WHITE_LABEL.ADD_SUB_DOMAIN.TITLE')}
        description={t('WHITE_LABEL.ADD_SUB_DOMAIN.DESCRIPTION')}
        opened={addSubDomainModalOpened}
        onClose={() => closeModal()}>
        <form
          onSubmit={addSubDomainForm.onSubmit((data) =>
            addSubDomainMutation.mutate({ domainId: domain._id, prefix: data.prefix })
          )}>
          <Flex direction="row" align="flex-start" w="100%" justify="space-around" gap={10}>
            <TextInput
              w="100%"
              withAsterisk
              label={t('WHITE_LABEL.ADD_SUB_DOMAIN.LABEL')}
              placeholder={t('WHITE_LABEL.ADD_SUB_DOMAIN.PLACEHOLDER')}
              {...addSubDomainForm.getInputProps('prefix')}
            />
            <Button loading={addSubDomainMutation.isLoading} mt={21} type="submit" size="sm" leftIcon={<IconPlus />}>
              {t('APP.ACTIONS.ADD')}
            </Button>
          </Flex>
        </form>
      </DroxyModal>
      <DroxyModal
        title={t('WHITE_LABEL.VERIFICATION_RECORD.TITLE')}
        description={t('WHITE_LABEL.VERIFICATION_RECORD.DESCRIPTION')}
        size="xl"
        titleIcon={<IconRosetteDiscountCheck />}
        opened={verificationDNSRecordModalOpened}
        onClose={() => setVerificationDNSRecordModalOpened(false)}>
        {domain.certificateVerificationDNSRecord && <DNSRecordTable record={domain.certificateVerificationDNSRecord} />}
      </DroxyModal>
      <ResourceConflictErrorModal
        errorModalOpened={errorModalOpened}
        conflictedSmartAssistants={conflictedSmartAssistants}
        onClose={() => setErrorModalOpened(false)}
      />
    </>
  );
}
